import {
  FC, useEffect, useMemo, useRef,
} from 'react';
import { useTranslation } from 'next-i18next';
import { usePreviewModeContext } from 'src/common/providers/PreviewModeProvider';
import getSnoozeStatus from 'src/common/utils/getSnoozeStatus';
import HeroImages from 'src/common/components/HeroImages';
import useUpdateDataToMatchCurrency from 'src/common/hooks/useUpdateDataToMatchCurrency';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import getClassNames from 'src/common/utils/getClassNames';
import classNames from 'classnames';
import useIntersectionObserver from 'src/common/hooks/useIntersectionObserver';
import { useSearchHeroVisibilityContext } from 'src/common/components/Search/SearchHeroVisibilityProvider';
import { RecentInquiryData } from 'src/common/utils/recentInquiryCookieData';
import type { ListingPageProps } from 'src/common/types/common/ListingPageProps';
import ListingOptionalAddons from 'src/addons/components/ListingOptionalAddons';
import BoatExperienceTags from 'src/listing/components/BoatExperienceTags';
import BoatDetailsAndTags from 'src/listing/components/BoatDetailsAndTags';
import { useListingMotivators } from 'src/listing/hooks/useListingPageMotivators';
import useBookingMotivatorsContext from 'src/common/providers/useBookingMotivators';
import { getDaysUntilInstabook } from 'src/common/utils/dateHelpers';
import { BOOKING_MOTIVATOR_STATES } from 'src/common/constants';
import BookingMotivator from 'src/common/components/BookingMotivator';
import { getAiSummaryDisplayData } from 'src/common/helpers';
import dynamic from 'next/dynamic';
import Description from 'src/listing/components/Description';
import PhotoGallery from 'src/common/components/PhotoGallery';
import useMounted from 'src/common/hooks/useMounted';
import useGalleryView from 'src/listing/hooks/useGalleryView';
import { trackEvent } from 'src/common/3rdParty/tracking';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import type { ListingCardBoat } from 'src/common/types';
import CancelPolicySummary from 'src/listing/components/CancelPolicySummary';
import { Breadcrumb, FullBoatSearchDetail } from '../../../common/types/api/v4/boats/search';
import AdditionalTerms from '../AdditionalTerms';
import AISummary from '../AISummary';
import BoatCapacityAndCategory from '../BoatCapacityAndCategories';
import BoatOwnerInfo from '../BoatOwnerInfo';
import BookingOverview from '../BookingOverview';
import CancellationPolicy from '../CancellationPolicy';
import CommunityPhotos from '../CommunityPhotos';
import ListingFavoriteButton from '../../../common/components/ListingFavoriteButton';
import LocationMap from '../LocationMap';
import Reviews from '../Reviews';
import ReviewsList from '../ReviewsList';
import SimilarListings from '../SimilarListings';
import TopDestinations from '../../../common/components/TopDestinations';
import SocialShare from '../../../common/components/SocialShare';
import { useRequestInfoContext } from '../../../common/providers/RequestInfoProvider';
import styles from './Listing.module.scss';
import CaptainOption from '../CaptainOption';

/**
 * Purpose of dynamic imports is to avoid loading the trip cart components
 * in the listing page depending on seasonal pricing being enabled or not.
 * This is to avoid the trip cart components being loaded when they are not needed.
 */
const TripCartRoot = dynamic(() => import('src/trip-cart/components/TripCartRoot'));
const TripCartRootDeprecated = dynamic(() => import('src/trip-cart-deprecated/components/TripCartRoot'));

type ListingProps = {
  listing: FullBoatSearchDetail;
  relatedListings: ListingCardBoat[];
  threadId?: string;
  previewMode?: boolean;
  ownerBoats?: FullBoatSearchDetail[];
  tripCartCookieData?: Partial<RecentInquiryData>;
  instabookDates: string[];
} & Pick<ListingPageProps, 'addonsOptional' | 'addonsLater'>;

const Listing: FC<ListingProps> = ({
  listing,
  relatedListings,
  threadId,
  previewMode,
  ownerBoats,
  tripCartCookieData,
  instabookDates,
  addonsOptional = [],
  addonsLater = [],
}) => {
  const [t] = useTranslation('admin-listing-preview');
  const pageViewTracked = useRef(false);
  const { setHeroInView } = useSearchHeroVisibilityContext();
  const { motivatorsDates, listingBlockedTimes, isListingMultiDayState } = useListingMotivators();
  const { getSelectedMotivators, getMultiSelectedMotivators } = useBookingMotivatorsContext();
  const { setToPreviewMode } = usePreviewModeContext();
  const { isMobileNativeWebView } = useMobileNativeWebView();
  const isMounted = useMounted();
  const { snoozeActive } = getSnoozeStatus(
    listing.snooze_start_date,
    listing.snooze_end_date,
    true,
  );

  const motivators = useMemo(() => {
    if (previewMode || !motivatorsDates.length) {
      return [];
    }
    return isListingMultiDayState
      ? getMultiSelectedMotivators(motivatorsDates)
      : getSelectedMotivators(motivatorsDates);
  }, [
    getSelectedMotivators,
    getMultiSelectedMotivators,
    motivatorsDates,
    previewMode,
    isListingMultiDayState]);

  const daysUntilInstabook = previewMode
    ? 0
    : getDaysUntilInstabook(motivatorsDates, instabookDates);

  const showMotivators = useMemo(
    () => motivators.some((motivator) => motivator !== BOOKING_MOTIVATOR_STATES.LOOKS_GOOD),
    [motivators],
  );

  const { protocol, host } = useRequestInfoContext();
  const listingUrl = `${protocol}//${host}${listing.listing_url}`;
  const boatOwner = listing.user_details;

  useUpdateDataToMatchCurrency(listing.request_currency.code, !previewMode);

  const {
    hasSummary,
    showAiSummary,
  } = useMemo(
    () => getAiSummaryDisplayData(listing.ai_summary),
    [listing.ai_summary],
  );

  // Track Listing page view after render, to ensure that the gtag.set function has been called.
  // This ensures that any AB Test data is available for the tracking event.
  const trackPageViewAfterRender = useAfterRenderCallback(() => {
    if (!pageViewTracked.current) {
      // GA already has a `listing_page_view` event, but it is set up inside GA and doesn't give
      // us the ability to add custom params. This additional event fills that gap, and is used
      // below to track whether a listing has AI Summary data from the API.
      trackEvent('listing_page_view_enhanced', {
        p1: showAiSummary ? 'ai_summary_visible' : 'ai_summary_hidden',
      });
      pageViewTracked.current = true;
    }
  });

  // Updates the components to function in preview mode when used within Owner Admin.
  useEffect(() => {
    if (previewMode) {
      setToPreviewMode();
    }
    trackPageViewAfterRender();
  }, [previewMode, setToPreviewMode, trackPageViewAfterRender]);

  /**
   * Used by MobileWebViewHeader to change background color and hide search
   * when HeroImages are 50% out of view
  */
  const heroImagesRef = useRef<HTMLDivElement | null>(null);
  useIntersectionObserver<HTMLDivElement>({
    threshold: 0.5,
    ref: heroImagesRef,
    callback: ([entry]) => {
      if (!isMobileNativeWebView) { return; }
      setHeroInView(entry.isIntersecting);
    },
  });

  const { isGalleryViewOpen } = useGalleryView();
  const isGalleryOpen = isGalleryViewOpen && isMounted;

  return (
    <>
      <div className={getClassNames(styles, 'root', classNames({ hidden: isGalleryOpen }))}>
        <div ref={heroImagesRef} className={styles.hero}>
          <HeroImages
            headline={listing.headline}
            listingId={listing.id}
            photos={listing.photos}
          />
        </div>
        <div className={styles.content}>
          <div className={getClassNames(styles, 'contentPanel', classNames({ isMobileNativeWebView }))}>
            <div className={styles.titleContainer}>
              <h1 className={styles.title}>
                {previewMode && !listing.headline
                  ? t('headline--placeholder')
                  : listing.h1 || listing.headline}
              </h1>
              {!previewMode && (
                <div className={styles.favoriteShareButtons}>
                  <SocialShare
                    title={listing.headline}
                    url={listingUrl}
                  />
                  {!isMobileNativeWebView
                  && (
                    <ListingFavoriteButton
                      listingId={listing.id}
                      cardLocation="listing page"
                    />
                  )}
                </div>
              )}
            </div>
            <div className={styles.headerRow}>
              <CaptainOption captainOptions={listing.captain_options} />
              {listing.cancellation_refund && (
                <>
                  <div className={styles.divider} />
                  <CancelPolicySummary
                    cancellation_refund={listing.cancellation_refund}
                    cancellation_anticipation={listing.cancellation_anticipation}
                  />
                </>
              )}
            </div>
            <BookingOverview
              averageRating={listing.rating_aggregates.rating_average}
              totalReviews={listing.total_reviews}
              totalBookings={listing.total_successful_bookings}
              breadcrumbs={listing.breadcrumbs}
              category={listing.cat_loc_breadcrumb as Breadcrumb}
              showBottomBorder={!showMotivators}
            />
            {/* Booking Motivators on listing page will only be shown for mobile view */}
            <div className={getClassNames(styles, 'listingMotivators', classNames({ showMotivators }))}>
              <BookingMotivator
                motivators={motivators}
                anticipatedDays={listing.cancellation_anticipation}
                daysUntilInstabook={daysUntilInstabook}
                size="m"
                blockedTimes={listingBlockedTimes}
              />
            </div>
            {showAiSummary && (
              <AISummary
                hasSummary={hasSummary}
                aiSummary={listing.ai_summary}
                listingId={listing.id}
              />
            )}
            <Description
              boatOwner={boatOwner}
              description={listing.description}
            />
            {addonsOptional.length > 0 && (
              <ListingOptionalAddons
                listingAddons={addonsOptional}
              />
            )}
            <BoatExperienceTags tagGroups={listing?.tag_groups} />
            <BoatOwnerInfo boatOwner={boatOwner} />

            <div className={styles.details}>
              <BoatCapacityAndCategory
                boat={listing}
                capacity={listing.capacity}
              />
            </div>
            <Reviews
              ratingAggregates={listing.rating_aggregates}
              totalReviews={listing.total_reviews}
            />
            <CommunityPhotos
              boatHeadline={listing.headline}
              communityPhotos={listing.community_photos}
              listingId={listing.id}
            />
            <ReviewsList
              reviews={listing.boat_reviews}
              reviewCount={listing.total_reviews}
              ratingAvg={listing.rating_aggregates.rating_average}
              listingId={listing.id}
            />
            <BoatOwnerInfo
              withDescription
              boatOwner={boatOwner}
              classNameModifier="listingPage"
              ownerBoats={ownerBoats}
            />
            <BoatDetailsAndTags
              tagGroups={listing?.tag_groups}
              make={listing.make}
              model={listing.model}
              capacity={listing.capacity}
            />
            <LocationMap
              locations={listing.locations}
            />
            <CancellationPolicy
              days={listing.cancellation_anticipation}
              percent={listing.cancellation_refund}
            />
            <AdditionalTerms terms={listing.cancellation_terms} />
            <SimilarListings
              listings={relatedListings}
              parentBreadcrumbs={listing.breadcrumbs}
            />
            <TopDestinations
              classNameModifier="listingPage"
              destinations={listing.nearby_destinations}
            />
          </div>
          {listing.has_calendar_pricing_enabled ? (
            <TripCartRoot
              listing={listing}
              tripCartCookieData={tripCartCookieData}
              previewMode={previewMode}
              snoozeActive={snoozeActive}
              threadId={threadId}
              addonsOptional={addonsOptional}
              addonsLater={addonsLater}
              showAiSummary={showAiSummary}
            />
          ) : (
            <TripCartRootDeprecated
              listing={listing}
              instabookDates={instabookDates}
              tripCartCookieData={tripCartCookieData}
              previewMode={previewMode}
              snoozeActive={snoozeActive}
              threadId={threadId}
              addonsOptional={addonsOptional}
              addonsLater={addonsLater}
              showAiSummary={showAiSummary}
            />
          )}
        </div>
      </div>
      {isGalleryOpen && (
        <PhotoGallery
          photos={listing.photos}
          headline={listing.headline}
          listingId={listing.id}
        />
      )}
    </>
  );
};

export default Listing;
